import { BrowserRouter as Router, Route, Outlet } from 'react-router-dom';
import { SentryRoutes } from '@/lib/sentry-routes';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from './context/AuthContext';
import { UserCacheProvider } from "./context/UserCacheContext";
import { useEffect, lazy, Suspense, Component, type ReactNode } from 'react';
import { tokenMonitor } from '@/services/tokenMonitoringService';
import { AnalyticsRouteTracker } from '@/components/analytics/AnalyticsRouteTracker';

import RoleProtectedRoute from "@/components/auth/ProtectedRoute";
import PublicRoute from "@/components/auth/PublicRoute";
import TokenRoute from "@/components/auth/TokenRoute";

// Eager — the pre-auth surfaces an unauthenticated (or locked-out) visitor
// reaches first. These stay in the initial chunk deliberately (KB-2614): no
// second round trip to render /login or /forgot-password.
import Index from "@/pages/Index";
import LoginPage from "@/components/auth/LoginPage";
import ForgotPasswordPage from "@/components/auth/ForgotPasswordPage";
import RequestAccountPage from "@/components/auth/RequestAccountPage";
import CreateAccountPage from '@/pages/CreateAccountPage';
import WelcomePage from '@/pages/WelcomePage';
import TermsOfUsePage from '@/pages/TermsOfUsePage';
import LogoutPage from '@/pages/LogoutPage';

// 🔥 NEW: Import App Check Debug Panel — mounted unconditionally inside
// AuthLayout below (not behind a route), so it isn't a code-splitting
// candidate and stays a regular import.
import { AppCheckDebugPanel } from '@/components/debug/AppCheckDebugPanel';

// Lazy — everything behind an admin/camp-manager role gate, plus the
// standalone calendar demo. Resolved through the single <Suspense> boundary
// around the authenticated <Outlet /> in AuthLayout below (KB-2614), the
// same fallback primitive MyGroupViewer already used pre-KB-2614.
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const AccountRequestsPage = lazy(() => import('@/components/admin/account-requests/AccountRequestsPage'));
const OrganizationsPage = lazy(() => import('@/components/admin/organizations/OrganizationsPage'));
const CreateSupportCasePage = lazy(() => import('@/components/support/CreateSupportCasePage'));
const SupportCasesPage = lazy(() => import('@/components/support/SupportCasesPage'));
const ViewSupportCasePage = lazy(() => import('@/components/support/ViewSupportCasePage'));
const OrganizationSupportTab = lazy(() => import('@/components/support/OrganizationSupportTab'));
const CampDataManager = lazy(() => import('@/components/admin/camp/CampDataManager'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const EditOrganizationPage = lazy(() => import('@/components/admin/organizations/EditOrganizationPage'));
const OrganizationLocationDetailsPage = lazy(() => import('@/components/admin/organizations/OrganizationLocationDetailsPage'));
const FieldsOptionsPage = lazy(() => import('@/components/admin/fields-options/FieldsOptionsPage'));
const EditSortableOptionPage = lazy(() => import('@/components/admin/fields-options/sortable-options/EditSortableOptionPage'));
const EditDecimalEnumPage = lazy(() => import('@/components/admin/fields-options/decimal-enums/EditDecimalEnumPage'));
const LocationsPage = lazy(() => import('@/components/admin/locations/LocationsPage'));
const ManageLocationPage = lazy(() => import('@/components/admin/locations/ManageLocationPage'));
const AdminSettingsPage = lazy(() => import('@/components/admin/settings/AdminSettingsPage'));
const ProfileSettings = lazy(() => import('@/pages/ProfileSettings'));
const OffersPage = lazy(() => import('@/components/admin/offers/OffersPage'));
const EditOfferPage = lazy(() => import('@/components/admin/offers/EditOfferPage'));
const UsersPage = lazy(() => import('@/components/admin/users/UsersPage'));
const UserDetailPage = lazy(() => import('@/components/admin/users/UserDetailPage'));
const DigestPreviewPage = lazy(() => import('@/components/admin/users/DigestPreviewPage'));
const DebugUserPage = lazy(() => import('@/components/admin/settings/DebugUserPage'));
const MetricsHealthPage = lazy(() => import('@/components/admin/metricsHealth/MetricsHealthPage'));
const TrafficReconciliationPage = lazy(() => import('@/components/admin/trafficReconciliation/TrafficReconciliationPage'));
const AdminReportsPage = lazy(() => import('@/components/admin/reports/AdminReportsPage'));
const GraphsPage = lazy(() => import('@/components/admin/graphs/GraphsPage'));
const OrgLocationsTreemapPage = lazy(() => import('@/components/admin/treemap/OrgLocationsTreemapPage'));
const EmailTemplatesPage = lazy(() => import('@/components/admin/email-templates/EmailTemplatesPage'));
const EmailTemplateDetailPage = lazy(() => import('@/components/admin/email-templates/EmailTemplateDetailPage'));
const EmailWrappersPage = lazy(() => import('@/components/admin/email-templates/EmailWrappersPage'));
const EmailWrapperDetailPage = lazy(() => import('@/components/admin/email-templates/EmailWrapperDetailPage'));
const EnqueuedEmailsPage = lazy(() => import('@/components/admin/email-templates/EnqueuedEmailsPage'));
const SystemEmailsPage = lazy(() => import('@/components/admin/system-emails/SystemEmailsPage'));
const SystemEmailDetailPage = lazy(() => import('@/components/admin/system-emails/SystemEmailDetailPage'));
const CommsPage = lazy(() => import('@/components/admin/comms/CommsPage'));
const OutreachPage = lazy(() => import('@/components/admin/outreach/OutreachPage'));
const OutreachCampaignPage = lazy(() => import('@/components/admin/outreach/OutreachCampaignPage'));
const MyGroupViewer = lazy(() => import('@/components/admin/tools/MyGroupViewer'));
const GroupSheetsPage = lazy(() => import('@/components/admin/groupSheets/GroupSheetsPage'));
const CampIngestLedgerPage = lazy(() => import('@/components/admin/campIngestLedger/CampIngestLedgerPage'));
const CampIngestLedgerIndexPage = lazy(() => import('@/components/admin/campIngestLedger/CampIngestLedgerIndexPage'));
const CampIngestLedgerAuditPage = lazy(() => import('@/components/admin/campIngestLedger/CampIngestLedgerAuditPage'));

import './App.css';

// Import test utilities for development
if (process.env.NODE_ENV === 'development') {
  import('@/utils/testSortableOptions');
}

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: true,
    },
  },
});

/**
 * KB-2614: catches a lazy route chunk that fails to fetch (a stale chunk hash
 * after a new deploy, a network blip) and shows a recoverable message instead
 * of an uncaught error unmounting the whole tree — React's default with no
 * boundary present. Pre-KB-2614 this failure mode existed but was narrow: only
 * MyGroupViewer was lazy, and its own Suspense had no boundary either. This
 * diff widens the exposure from that one route to every admin/camp-manager
 * route, so it's the right place to close the gap. A reload — not client-side
 * retry — is the correct recovery: a stale chunk hash means the browser's
 * already-loaded module graph doesn't match the current deploy, so only a
 * fresh fetch of index.html (and its manifest) can find the right file.
 */
class RouteErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-8 text-center">
          <p className="mb-4">Something went wrong loading this page.</p>
          <button
            type="button"
            className="text-blue-500 hover:text-blue-700 underline"
            onClick={() => window.location.reload()}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * Authenticated app shell: mounts the auth + user-cache providers — and thus
 * the AuthContext "Loading your session…" gate — around every non-rescue route.
 * Rendered via a PATHLESS layout route so its children keep their existing
 * absolute paths unchanged. `/logout` is intentionally a SIBLING of this layout
 * (see App), so it renders without waiting on AuthContext to resolve (KB-1670).
 */
function AuthLayout() {
  return (
    <AuthProvider>
      <UserCacheProvider>
        {/* One Suspense boundary covers every lazy route (KB-2614), wrapped in
            an error boundary for a failed chunk fetch (see RouteErrorBoundary
            above). The routed page is the only thing that can suspend or throw
            here — AnalyticsRouteTracker, Toaster, and AppCheckDebugPanel are
            siblings of Outlet, not children, so a pending or failed route
            import never delays or takes them down. */}
        <RouteErrorBoundary>
          <Suspense fallback={<div className="p-8">Loading…</div>}>
            <Outlet />
          </Suspense>
        </RouteErrorBoundary>
        <AnalyticsRouteTracker />
        <Toaster />
        {/* App Check Debug Panel - helps debug token expiration issues */}
        <AppCheckDebugPanel />
      </UserCacheProvider>
    </AuthProvider>
  );
}

function App() {
  // Initialize token monitoring service on app startup
  useEffect(() => {
    console.log('[App] Initializing token monitoring service');
    tokenMonitor.startMonitoring();
    
    // Cleanup on unmount
    return () => {
      console.log('[App] Cleaning up token monitoring service');
      tokenMonitor.stopMonitoring();
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <SentryRoutes>
          {/* Rescue hatch — MUST render OUTSIDE the AuthContext "Loading your
              session…" gate so it recovers a session wedged on auth bootstrap.
              It is a SIBLING of the auth-gated layout below, not a child, so it
              never waits on AuthContext to resolve. (KB-1670) */}
          <Route path="/logout" element={<LogoutPage />} />

          {/* Everything else renders inside the auth providers — and thus
              behind the AuthContext loading gate — exactly as before. */}
          <Route element={<AuthLayout />}>
              {/* Public Routes */}
              <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
              <Route path="/create-account" element={<PublicRoute><CreateAccountPage /></PublicRoute>} />
              <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
              <Route path="/request-account" element={<PublicRoute><RequestAccountPage /></PublicRoute>} />
              <Route path="/welcome" element={<TokenRoute><WelcomePage /></TokenRoute>} />
              <Route path="/terms-of-use" element={<PublicRoute><TermsOfUsePage /></PublicRoute>} />
              
              {/* Auth Check & Redirect Route */}
              <Route path="/" element={<Index />} />
              
              {/* Protected Routes - now admin only */}
              <Route path="/dashboard" element={<RoleProtectedRoute requiredRoles={["admin_user"]}><Dashboard /></RoleProtectedRoute>} />
              <Route path="/profile" element={<RoleProtectedRoute requiredRoles={["admin_user"]}><ProfileSettings /></RoleProtectedRoute>} />
              <Route path="/organizations" element={<RoleProtectedRoute requiredRoles={["admin_user"]}><OrganizationsPage /></RoleProtectedRoute>} />
              <Route path="/organizations/:id/edit" element={<RoleProtectedRoute requiredRoles={["admin_user", "camp_manager"]}><EditOrganizationPage /></RoleProtectedRoute>} />
              <Route path="/organizations/:id/edit/locations/details" element={<RoleProtectedRoute requiredRoles={["admin_user", "camp_manager"]}><OrganizationLocationDetailsPage /></RoleProtectedRoute>} />
              <Route path="/organizations/:id/support" element={<RoleProtectedRoute requiredRoles={["admin_user"]}><OrganizationSupportTab /></RoleProtectedRoute>} />
              <Route path="/locations" element={<RoleProtectedRoute requiredRoles={["admin_user"]}><LocationsPage /></RoleProtectedRoute>} />
              <Route path="/locations/:id/edit" element={<RoleProtectedRoute requiredRoles={["admin_user"]}><ManageLocationPage /></RoleProtectedRoute>} />
              <Route path="/offers" element={<RoleProtectedRoute requiredRoles={["admin_user"]}><OffersPage /></RoleProtectedRoute>} />
              <Route path="/offers/:id/edit" element={<RoleProtectedRoute requiredRoles={["admin_user"]}><EditOfferPage /></RoleProtectedRoute>} />
              <Route path="/users" element={<RoleProtectedRoute requiredRoles={["admin_user"]}><UsersPage /></RoleProtectedRoute>} />
              <Route path="/users/:id" element={<RoleProtectedRoute requiredRoles={["admin_user"]}><UserDetailPage /></RoleProtectedRoute>} />
              <Route path="/users/:id/digest-preview" element={<RoleProtectedRoute requiredRoles={["admin_user"]}><DigestPreviewPage /></RoleProtectedRoute>} />

              {/* Admin Routes (admin_user only) */}
              <Route path="/account-requests" element={<RoleProtectedRoute requiredRoles={["admin_user"]}><AccountRequestsPage /></RoleProtectedRoute>} />
              {/* KB-2511 [T23] — nightly analytics invariant-monitor verdicts. */}
              <Route path="/admin/metrics-health" element={<RoleProtectedRoute requiredRoles={["admin_user"]}><MetricsHealthPage /></RoleProtectedRoute>} />
              {/* KB-2512 [T24] — L1-L4 side by side, one row per ET day. */}
              <Route path="/admin/traffic-reconciliation" element={<RoleProtectedRoute requiredRoles={["admin_user"]}><TrafficReconciliationPage /></RoleProtectedRoute>} />
              <Route path="/admin-reports" element={<RoleProtectedRoute requiredRoles={["admin_user"]}><AdminReportsPage /></RoleProtectedRoute>} />
              <Route path="/graphs" element={<RoleProtectedRoute requiredRoles={["admin_user"]}><GraphsPage /></RoleProtectedRoute>} />
              <Route path="/treemap" element={<RoleProtectedRoute requiredRoles={["admin_user"]}><OrgLocationsTreemapPage /></RoleProtectedRoute>} />
              <Route path="/fields-options" element={<RoleProtectedRoute requiredRoles={["admin_user"]}><FieldsOptionsPage /></RoleProtectedRoute>} />
              <Route path="/fields-options/sortable-options/:id" element={<RoleProtectedRoute requiredRoles={["admin_user"]}><EditSortableOptionPage /></RoleProtectedRoute>} />
              <Route path="/fields-options/decimal-enums/:id" element={<RoleProtectedRoute requiredRoles={["admin_user"]}><EditDecimalEnumPage /></RoleProtectedRoute>} />
              <Route path="/email-templates" element={<RoleProtectedRoute requiredRoles={["admin_user"]}><EmailTemplatesPage /></RoleProtectedRoute>} />
              <Route path="/email-templates/enqueued" element={<RoleProtectedRoute requiredRoles={["admin_user"]}><EnqueuedEmailsPage /></RoleProtectedRoute>} />
              <Route path="/email-templates/:id" element={<RoleProtectedRoute requiredRoles={["admin_user"]}><EmailTemplateDetailPage /></RoleProtectedRoute>} />
              <Route path="/email-wrappers" element={<RoleProtectedRoute requiredRoles={["admin_user"]}><EmailWrappersPage /></RoleProtectedRoute>} />
              <Route path="/email-wrappers/:id" element={<RoleProtectedRoute requiredRoles={["admin_user"]}><EmailWrapperDetailPage /></RoleProtectedRoute>} />
              <Route path="/system-emails" element={<RoleProtectedRoute requiredRoles={["admin_user"]}><SystemEmailsPage /></RoleProtectedRoute>} />
              <Route path="/system-emails/:id" element={<RoleProtectedRoute requiredRoles={["admin_user"]}><SystemEmailDetailPage /></RoleProtectedRoute>} />
              <Route path="/comms" element={<RoleProtectedRoute requiredRoles={["admin_user"]}><CommsPage /></RoleProtectedRoute>} />
              <Route path="/outreach" element={<RoleProtectedRoute requiredRoles={["admin_user"]}><OutreachPage /></RoleProtectedRoute>} />
              <Route path="/outreach/:id" element={<RoleProtectedRoute requiredRoles={["admin_user"]}><OutreachCampaignPage /></RoleProtectedRoute>} />
              <Route path="/settings" element={<RoleProtectedRoute requiredRoles={["admin_user"]}><AdminSettingsPage /></RoleProtectedRoute>} />
              <Route path="/debug-user" element={<RoleProtectedRoute requiredRoles={["admin_user"]}><DebugUserPage /></RoleProtectedRoute>} />
              <Route path="/support" element={<RoleProtectedRoute requiredRoles={["admin_user"]}><SupportCasesPage /></RoleProtectedRoute>} />
              <Route path="/support/create" element={<RoleProtectedRoute requiredRoles={["admin_user"]}><CreateSupportCasePage /></RoleProtectedRoute>} />
              <Route path="/support/:id" element={<RoleProtectedRoute requiredRoles={["admin_user"]}><ViewSupportCasePage /></RoleProtectedRoute>} />
              <Route path="/camp-data" element={<RoleProtectedRoute requiredRoles={["admin_user"]}><CampDataManager /></RoleProtectedRoute>} />
              <Route path="/admin/my-group-viewer" element={<RoleProtectedRoute requiredRoles={["admin_user"]}><MyGroupViewer /></RoleProtectedRoute>} />
              <Route path="/admin/group-sheets" element={<RoleProtectedRoute requiredRoles={["admin_user"]}><GroupSheetsPage /></RoleProtectedRoute>} />
              <Route path="/admin/camp-ingest-ledger" element={<RoleProtectedRoute requiredRoles={["admin_user"]}><CampIngestLedgerIndexPage /></RoleProtectedRoute>} />
              <Route path="/admin/camp-ingest-ledger/:ledgerId" element={<RoleProtectedRoute requiredRoles={["admin_user"]}><CampIngestLedgerPage /></RoleProtectedRoute>} />
              <Route path="/admin/camp-ingest-ledger/:ledgerId/audit" element={<RoleProtectedRoute requiredRoles={["admin_user"]}><CampIngestLedgerAuditPage /></RoleProtectedRoute>} />
              <Route path="*" element={<RoleProtectedRoute requiredRoles={["admin_user"]}><NotFound /></RoleProtectedRoute>} />
          </Route>
        </SentryRoutes>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
